/// <amd-module name="Reporting/Visuals/LayoutComponents/Widgets/ScalarComponent" />
import * as React from "react";

import { DemandType } from "Widgets/Demands/DemandType";
import { IDemandHandler } from "Widgets/Demands/IDemandHandler";

/**
 * Stateless parameters (creation-time) for constructing a ScalarComponent (e.g. a "Hero Metric")
 */
export class ScalarComponentProps {
    /** Numeric value to present for this metric. */
    public value: string | number;

    /** A localized, user facing description of the unit of measurement.
     * What benchmark are we *aggregating by*, to create this metric?
     * e.g. "Story Points"
     */
    public measure: string;

    /** A localized, user facing description of meaning behind the number being presented.
     * Where did the data we are presenting to the user come from?
     * e.g. "Average Velocity"
     */
    public description: string;
}

export const ScalarHorizontalComponent: React.StatelessComponent<ScalarComponentProps> = (props: ScalarComponentProps) => {
    const scalarSize = ScalarComponentHelper.getScalarValueSize(props.value);
    const localizedValue = props.value
        ? typeof props.value === "string"
            ? props.value
            : Number(+props.value).toLocaleString(navigator.language)
        : props.value;

    const scalarSizeCssClass = scalarSize + "-scalar-size";

    return scalarSize === ScalarSize[ScalarSize.small] ? (
        <div className={"scalar-component " + scalarSizeCssClass + " horizontal-mode"}>
            <div className={"scalar-component-value " + scalarSizeCssClass}>{localizedValue}</div>
            <div className={"scalar-component-text " + scalarSizeCssClass}>
                <div className={"scalar-component-description " + scalarSizeCssClass}>{props.measure + ", " + props.description}</div>
            </div>
        </div>
    ) : (
        <div className={"scalar-component " + scalarSizeCssClass + " horizontal-mode"}>
            <div className={"scalar-component-text " + scalarSizeCssClass}>
                <div className={"scalar-component-measure " + scalarSizeCssClass}>{props.measure}</div>
                <div className={"scalar-component-description " + scalarSizeCssClass}>{props.description}</div>
            </div>
            <div className={"scalar-component-value " + scalarSizeCssClass}>{localizedValue}</div>
        </div>
    );
};

export const ScalarVerticalComponent: React.StatelessComponent<ScalarComponentProps> = (props: ScalarComponentProps) => {
    const scalarSize = ScalarComponentHelper.getScalarValueSize(props.value);
    const scalarSizeCssClass = scalarSize + "-scalar-size";

    return (
        <div className="scalar-component vertical-mode">
            <div className={"scalar-component-value " + scalarSizeCssClass}>{props.value}</div>
            <div className="scalar-component-text">
                <div className="scalar-component-measure">{props.measure}</div>
                <div className="scalar-component-description">{props.description}</div>
            </div>
        </div>
    );
};

export default class ScalarComponentRenderer {
    public static registerDemands(demandHandler: IDemandHandler): void {
        demandHandler.registerDemand(DemandType.scalar);
    }

    public static renderScalarHorizontalComponent(props: ScalarComponentProps | undefined): React.ReactElement<any> | null {
        return props != undefined ? <ScalarHorizontalComponent {...props} /> : null;
    }

    public static renderScalarVerticalComponent(props: ScalarComponentProps | undefined): React.ReactElement<any> | null {
        return props != undefined ? <ScalarVerticalComponent {...props} /> : null;
    }
}

enum ScalarSize {
    small,
    large
}

class ScalarComponentHelper {
    // Size of the scalar value element depends on the number of digits in the number.
    // Currently, if the value exceeds 4 digits, we resize from 72px to 16px
    public static getScalarValueSize(value: number | string): string {
        return value && value.toString().length > 4 ? ScalarSize[ScalarSize.small] : ScalarSize[ScalarSize.large];
    }
}
